import { PrescriptionTemplate } from "@/fluxvet/hooks/usePrescriptionTemplates";

/**
 * Converte um QuestionnaireResponse FHIR R5 para PrescriptionTemplate
 * 
 * @param questionnaireResponse - QuestionnaireResponse do FHIR
 * @returns PrescriptionTemplate
 */
export function convertQuestionnaireResponseToTemplate(
  questionnaireResponse: fhir5.QuestionnaireResponse
): PrescriptionTemplate {
  const template: PrescriptionTemplate = {
    id: questionnaireResponse.id || "",
    name: "",
    createdAt: questionnaireResponse.authored || new Date().toISOString(),
    updatedAt: questionnaireResponse.meta?.lastUpdated || new Date().toISOString(),
  };

  // Processa os itens do QuestionnaireResponse
  const items = questionnaireResponse.item || [];

  // Extrai informações do modelo
  const templateInfo = items.find((item) => item.linkId === "template-info");
  if (templateInfo?.item) {
    const nameItem = templateInfo.item.find((i) => i.linkId === "template-name");
    const descItem = templateInfo.item.find((i) => i.linkId === "template-description");

    template.name = nameItem?.answer?.[0]?.valueString || "";
    template.description = descItem?.answer?.[0]?.valueString;
  }

  // Extrai tratamentos
  const treatmentItems = items.filter((item) => item.linkId === "treatments");
  if (treatmentItems.length > 0) {
    template.treatments = treatmentItems.map((treatmentGroup) => {
      const nameItem = treatmentGroup.item?.find((i) => i.linkId === "treatment-name");
      const dosageItem = treatmentGroup.item?.find((i) => i.linkId === "treatment-dosage");
      const descItem = treatmentGroup.item?.find((i) => i.linkId === "treatment-description");

      return {
        name: nameItem?.answer?.[0]?.valueString || "",
        dosage: dosageItem?.answer?.[0]?.valueString || "",
        description: descItem?.answer?.[0]?.valueString || "",
      };
    });
  }

  // Extrai exames/procedimentos
  const examItems = items.filter((item) => item.linkId === "exams");
  if (examItems.length > 0) {
    template.examsOrProcedures = examItems.map((examGroup) => {
      const descItem = examGroup.item?.find((i) => i.linkId === "exam-description");
      const notesItem = examGroup.item?.find((i) => i.linkId === "exam-notes");

      return {
        description: descItem?.answer?.[0]?.valueString || "",
        notes: notesItem?.answer?.[0]?.valueString || "",
      };
    });
  }

  // Extrai relatórios
  const reportItems = items.filter((item) => item.linkId === "reports");
  if (reportItems.length > 0) {
    template.reports = reportItems.map((reportGroup) => {
      const contentItem = reportGroup.item?.find((i) => i.linkId === "report-content");

      return {
        content: contentItem?.answer?.[0]?.valueString || "",
      };
    });
  }

  // Extrai orientações
  const guidelinesItem = items.find((item) => item.linkId === "guidelines");
  if (guidelinesItem?.answer?.[0]?.valueString) {
    template.guidelines = guidelinesItem.answer[0].valueString;
  }

  // Questionários salvos no protocolo (ver PostPrescriptionTemplate / linkId template-questionnaire).
  const questionnaireGroups = items.filter(
    (item) => item.linkId === "template-questionnaire" && item.item?.length
  );
  if (questionnaireGroups.length > 0) {
    template.questionnaires = questionnaireGroups
      .map((group) => {
        const read = (linkId: string) =>
          group.item?.find((i) => i.linkId === linkId)?.answer?.[0]?.valueString;
        const questionnaireId = read("tq-questionnaire-id")?.trim() || "";
        const title = read("tq-title")?.trim() || "";
        const name = read("tq-name")?.trim() || title;
        const label = read("tq-label")?.trim() || questionnaireId;
        const usageType = read("tq-usage-type")?.trim();
        if (!questionnaireId) return null;
        return {
          type: "questionnaire" as const,
          questionnaireId,
          title: title || name,
          name: name || title,
          label,
          ...(usageType ? { usageType } : {}),
        };
      })
      .filter((q): q is NonNullable<typeof q> => q !== null);
  }

  return template;
}

/**
 * Converte um Bundle FHIR com QuestionnaireResponse para array de PrescriptionTemplate
 * 
 * @param bundle - Bundle FHIR do servidor
 * @returns Array de PrescriptionTemplate
 */
export function convertBundleToTemplates(bundle: fhir5.Bundle): PrescriptionTemplate[] {
  if (!bundle.entry || bundle.entry.length === 0) {
    return [];
  }

  return bundle.entry
    .filter((entry) => entry.resource?.resourceType === "QuestionnaireResponse")
    .map((entry) => convertQuestionnaireResponseToTemplate(entry.resource as fhir5.QuestionnaireResponse))
    .filter((template) => template.name); // Remove templates inválidos
}
